import type { SavePayoutPolicyDto } from "@fp/admin-api-client";
import { useGetOrganizationContextCapabilities } from "@fp/admin-api-client/organization-context/organization-context.client";
import {
  getGetPayoutPolicyQueryKey,
  getListPayeesOnLegacyTermsQueryKey,
  getListPayoutPoliciesQueryKey,
  useDeletePayoutPolicy,
  useGetPayoutPolicy,
  useMarkPayoutPolicyDefault,
  useUpdatePayoutPolicy,
} from "@fp/admin-api-client/payout-policies/payout-policies.client";
import { DestructiveConfirmationModal } from "@fp/ui/components/application/modals/destructive-confirmation-modal";
import { Button } from "@fp/ui/components/base/buttons/button";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Trash01 } from "@untitledui/icons";
import type { AxiosError } from "axios";
import { useState } from "react";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import type { ApiErrorResponse } from "@/lib/axios";
import { describeEarlyDisbursement, describeReleaseTiming } from "@/lib/payout-terms";
import { toast } from "@/lib/toast";
import { PayeesOnLegacyTerms } from "./-components/payees-on-legacy-terms";
import { PayoutPolicyEditor } from "./-components/payout-policy-editor";
import { redirectUnlessPayoutPolicyEnabled, reportPayoutPolicyError } from "./-components/payout-policy-gate";
import { PayoutPolicyHelp } from "./-components/payout-policy-help";

export const Route = createFileRoute("/settings/payout-policies/$id")({
  beforeLoad: ({ context: { queryClient } }) => redirectUnlessPayoutPolicyEnabled(queryClient),
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const { data: policyResponse } = useGetPayoutPolicy(id);
  const { data: capabilitiesResponse } = useGetOrganizationContextCapabilities();
  const policy = policyResponse?.data;

  const updateMutation = useUpdatePayoutPolicy({
    mutation: {
      onSuccess: (response) => {
        queryClient.setQueryData(getGetPayoutPolicyQueryKey(id), response);
        queryClient.invalidateQueries({ queryKey: getListPayoutPoliciesQueryKey() });
        queryClient.invalidateQueries({ queryKey: getListPayeesOnLegacyTermsQueryKey(id) });
        toast.success(`${response.data.name} was saved. Payees already on it keep their terms until you move them.`);
      },
      onError: (err) => reportPayoutPolicyError(err as AxiosError<ApiErrorResponse>, "Failed to save the payout policy", queryClient, navigate),
    },
  });

  const markDefaultMutation = useMarkPayoutPolicyDefault({
    mutation: {
      onSuccess: (response) => {
        queryClient.setQueryData(getGetPayoutPolicyQueryKey(id), response);
        queryClient.invalidateQueries({ queryKey: getListPayoutPoliciesQueryKey() });
        toast.success(`${response.data.name} is now the default for payees who join`);
      },
      onError: (err) => reportPayoutPolicyError(err as AxiosError<ApiErrorResponse>, "Failed to make the payout policy the default", queryClient, navigate),
    },
  });

  const deleteMutation = useDeletePayoutPolicy({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListPayoutPoliciesQueryKey() });
        toast.success(`${policy?.name ?? "The payout policy"} was deleted`);
        navigate({ to: "/settings/payout-policies" });
      },
      onError: (err) => {
        setIsDeleteConfirmOpen(false);
        reportPayoutPolicyError(err as AxiosError<ApiErrorResponse>, "Failed to delete the payout policy", queryClient, navigate);
      },
    },
  });

  return (
    <AdminLayout>
      <AdminPageHeader
        title={policy?.name ?? "Payout Policy"}
        parent={{ url: "/settings/payout-policies", label: "Payout Policies" }}
        titleActions={<PayoutPolicyHelp maximumFeeRate={policy?.maximumFeeRate ?? 0} />}
      />
      {policy && (
        <PayoutPolicyEditor
          policy={policy}
          maximumFeeRate={policy.maximumFeeRate}
          isSaving={updateMutation.isPending}
          saveLabel="Save Payout Policy"
          onSave={(body: SavePayoutPolicyDto) => updateMutation.mutate({ id, data: body })}
        >
          <PayeesOnLegacyTerms policy={policy} canMovePayees={capabilitiesResponse?.data.canAssignTalentPayoutPolicy ?? false} />
          {!policy.isDefault && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <p className="text-sm text-gray-600">Payees who join the roster receive the default policy&apos;s terms.</p>
              <Button color="secondary" size="sm" className="shrink-0" onClick={() => markDefaultMutation.mutate({ id })} isLoading={markDefaultMutation.isPending}>
                Make default
              </Button>
            </div>
          )}
          {!policy.isSystem && (
            <div>
              <h3 className="mb-3 text-lg font-semibold text-red-600">Danger Zone</h3>
              <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 p-4">
                <div>
                  <h4 className="font-medium text-gray-900">Delete this policy</h4>
                  <p className="text-sm text-gray-600">Payees on this policy keep its terms.</p>
                </div>
                <Button color="secondary-destructive" size="sm" className="shrink-0" iconLeading={Trash01} onClick={() => setIsDeleteConfirmOpen(true)}>
                  Delete
                </Button>
              </div>
            </div>
          )}
        </PayoutPolicyEditor>
      )}
      {policy && (
        <DestructiveConfirmationModal
          isOpen={isDeleteConfirmOpen}
          onClose={() => setIsDeleteConfirmOpen(false)}
          onConfirm={() => deleteMutation.mutate({ id })}
          title="Delete Payout Policy"
          entity={{
            label: policy.name,
            description: `${describeReleaseTiming(policy.releaseTiming)} · ${describeEarlyDisbursement(policy.earlyDisbursement)}`,
          }}
          impactText={
            policy.isDefault
              ? "Payees on this policy keep its terms. Release Immediately becomes the default for payees who join."
              : "Payees on this policy keep its terms. They can be assigned another policy from the roster."
          }
          isLoading={deleteMutation.isPending}
        />
      )}
    </AdminLayout>
  );
}